import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, UserRound, Eye } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const empty = { email: "", password: "", name: "", role: "empleado" };
    const [form, setForm] = useState(empty);

    const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.email || !form.password || !form.name) return toast.error("Email, nombre y contraseña son obligatorios");
        if (form.password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
        try {
            await api.post("/auth/users", form);
            toast.success("Usuario creado");
            setOpen(false); setForm(empty); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al crear usuario");
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`¿Eliminar usuario ${u.email}?`)) return;
        try {
            await api.delete(`/auth/users/${u.id}`);
            toast.success("Usuario eliminado");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al eliminar");
        }
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Administración</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Usuarios</h1>
                    <p className="text-sm text-slate-500 mt-1">Crea cuentas para empleados. Los admins tienen acceso total; los empleados pueden crear y editar pero no eliminar; los lectores solo pueden ver, sin poder crear, editar ni eliminar.</p>
                </div>
                <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-user-button">
                    <Plus size={16} className="mr-1" /> Nuevo usuario
                </Button>
            </header>

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium">{users.length} usuario(s)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                                    <th className="text-left px-4 py-3 font-semibold">Email</th>
                                    <th className="text-left px-4 py-3 font-semibold">Rol</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {users.length === 0 && <tr><td colSpan={4} className="text-center px-4 py-10 text-slate-400">Sin usuarios</td></tr>}
                                {users.map((u) => (
                                    <tr key={u.id} className="border-t border-slate-100" data-testid={`user-${u.id}`}>
                                        <td className="px-4 py-3 font-medium text-slate-900">
                                            <span className="inline-flex items-center gap-2">
                                                {u.role === "admin" ? <ShieldCheck size={14} className="text-emerald-600" /> : u.role === "lector" ? <Eye size={14} className="text-sky-600" /> : <UserRound size={14} className="text-slate-500" />}
                                                {u.name}
                                                {u.id === me?.id && <Badge variant="secondary" className="ml-1">tú</Badge>}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-700">{u.email}</td>
                                        <td className="px-4 py-3">
                                            {u.role === "admin"
                                                ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Administrador</Badge>
                                                : u.role === "lector"
                                                ? <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Lector (solo lectura)</Badge>
                                                : <Badge variant="secondary">Empleado</Badge>}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {u.id !== me?.id && (
                                                <button onClick={() => remove(u)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`del-user-${u.id}`}>
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Nuevo usuario</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Nombre</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
                        </div>
                        <div>
                            <Label className="text-xs">Email</Label>
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
                        </div>
                        <div>
                            <Label className="text-xs">Contraseña inicial</Label>
                            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                            <p className="text-xs text-slate-500 mt-1">Mínimo 6 caracteres. El usuario podrá cambiarla después.</p>
                        </div>
                        <div>
                            <Label className="text-xs">Rol</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="empleado">Empleado (sin permiso de borrar)</SelectItem>
                                    <SelectItem value="lector">Lector (solo puede ver, sin crear/editar/borrar)</SelectItem>
                                    <SelectItem value="admin">Administrador (acceso total)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-user">Crear</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
